import { kebabCase } from 'lodash';
import { pascalCase } from 'utils/pascal-case';
import { unreachable } from 'utils/unreachable';
import type { SingleSelectField } from '../fields';
import {
  SelectFilterOperationType,
  valueFromString,
  valueToString,
} from './select';
import { TermSeparator } from './separators';

interface SingleSelectFieldFilterBase {
  field: SingleSelectField;
}

interface SingleSelectFieldValueFilterBase extends SingleSelectFieldFilterBase {
  value?: number;
}

interface SingleSelectMultiFieldValueFilterBase
  extends SingleSelectFieldFilterBase {
  value?: number[];
}

interface SingleSelectIsFilter extends SingleSelectFieldValueFilterBase {
  type: SelectFilterOperationType.Is;
}

interface SingleSelectIsNotFilter extends SingleSelectFieldValueFilterBase {
  type: SelectFilterOperationType.IsNot;
}

interface SingleSelectIsAtLeastFilter extends SingleSelectFieldValueFilterBase {
  type: SelectFilterOperationType.IsAtLeast;
}

interface SingleSelectIsAtMostFilter extends SingleSelectFieldValueFilterBase {
  type: SelectFilterOperationType.IsAtMost;
}

interface SingleSelectIsOneOfFilter
  extends SingleSelectMultiFieldValueFilterBase {
  type: SelectFilterOperationType.IsOneOf;
}

interface SingleSelectIsNoneOfFilter
  extends SingleSelectMultiFieldValueFilterBase {
  type: SelectFilterOperationType.IsNoneOf;
}

interface SingleSelectIsEmptyFilter extends SingleSelectFieldFilterBase {
  type: SelectFilterOperationType.IsEmpty;
}

interface SingleSelectIsNotEmptyFilter extends SingleSelectFieldFilterBase {
  type: SelectFilterOperationType.IsNotEmpty;
}

export type SingleSelectFieldFilter =
  | SingleSelectIsFilter
  | SingleSelectIsNotFilter
  | SingleSelectIsOneOfFilter
  | SingleSelectIsNoneOfFilter
  | SingleSelectIsEmptyFilter
  | SingleSelectIsNotEmptyFilter
  | SingleSelectIsAtLeastFilter
  | SingleSelectIsAtMostFilter;

const isValid = (filter: SingleSelectFieldFilter) => {
  switch (filter.type) {
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
      return filter.value != null;
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return (filter.value ?? []).length > 0;
    default:
      return true;
  }
};

const to = valueToString<number>((x) => x.toString());
const from = valueFromString((value) => {
  const result = parseInt(value, 10);

  // NOTE:: for inferredProgress the value will be string and we need to return the string.
  // Don't convert strings like `1 String` to 1. So added Number.isNaN(Number(value))
  return Number.isNaN(result) || Number.isNaN(Number(value))
    ? (value as unknown as number)
    : result;
});

const toString = (filter: SingleSelectFieldFilter): string => {
  const type = kebabCase(filter.type);

  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return type;
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
      return [type, to(filter.value == null ? [] : [filter.value])].join(
        TermSeparator,
      );
    case SelectFilterOperationType.IsNoneOf:
    case SelectFilterOperationType.IsOneOf:
      return [type, to(filter.value ?? [])].join(TermSeparator);
    default:
      return unreachable(filter);
  }
};

const parse = (
  value: string,
  field: SingleSelectField,
): SingleSelectFieldFilter => {
  const [type_string, meta] = value.split(TermSeparator);
  const type = pascalCase(type_string) as SelectFilterOperationType;

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
      return { field, type, value: (from(meta) ?? [])[0] };
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      return { field, type, value: from(meta) };
    default:
      return unreachable(type);
  }
};

export const SingleSelectFieldFilter = {
  isValid,
  toString,
  parse,
};
